import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "./gj";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = authed
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 on first load when not signed in — expected
      if (err?.response?.status && err.response.status !== 401) {
        console.error("[auth] fetchMe failed:", err);
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      console.error("[auth] login failed:", {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      });
      const friendly =
        formatApiError(e?.response?.data?.detail) ||
        (e?.response?.status ? `Server error (${e.response.status}). Please try again.` : null) ||
        e?.message ||
        "Could not sign in. Check your internet and try again.";
      return { ok: false, error: friendly };
    }
  }, []);

  const register = useCallback(async (payload) => {
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      // Surface as much info as we can for diagnostics
      console.error("[auth] register failed:", {
        status: e?.response?.status,
        data: e?.response?.data,
        message: e?.message,
      });
      const friendly =
        formatApiError(e?.response?.data?.detail) ||
        (e?.response?.status ? `Server error (${e.response.status}). Please try again.` : null) ||
        e?.message ||
        "Could not create account. Check your internet and try again.";
      return { ok: false, error: friendly };
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("[auth] logout request failed:", err);
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, loading, login, register, logout, refresh: fetchMe }),
    [user, loading, login, register, logout, fetchMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
